import React, { useCallback, useEffect, useState } from "react";
import { http, inr, requestErrorMessage, waLink } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Search, Receipt, Pencil, Trash2, Send, Paperclip, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toLocaleDateString("en-CA");
const CATEGORIES = ["travel", "employee", "office", "marketing", "utilities", "rent", "logistics", "misc"];
const MODES = ["Cash", "Bank", "UPI", "Card", "Cheque"];
const TRAVEL = ["bike", "car", "bus", "train", "flight", "auto"];
const EMPTY = { expense_date: today(), category: "office", expense_type: "", payee_type: "other", payee_name: "", description: "", travel_mode: "", distance_km: "", amount: "", gst_pct: 0, payment_mode: "Cash", notes: "" };

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try { const r = await http.get("/expenses", { params: { search } }); setRows(r.data); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, "load expenses")); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const mileage = form.category === "travel" && ["bike", "car"].includes(form.travel_mode);
  const baseAmt = mileage ? Math.round(Number(form.distance_km || 0) * (form.travel_mode === "bike" ? 3.25 : 6) * 100) / 100 : Number(form.amount || 0);
  const gstAmt = Math.round(baseAmt * Number(form.gst_pct || 0) / 100 * 100) / 100;

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (e) => { setForm({ ...EMPTY, ...e }); setEditId(e.id); setOpen(true); };

  const save = async () => {
    if (!form.description.trim()) return toast.error("Enter a description");
    if (baseAmt <= 0) return toast.error(mileage ? "Enter distance above zero" : "Enter an amount above zero");
    try {
      const payload = { ...form, amount: form.amount === "" ? 0 : Number(form.amount), gst_pct: Number(form.gst_pct || 0), distance_km: form.distance_km === "" ? null : Number(form.distance_km) };
      if (editId) await http.put(`/expenses/${editId}`, payload);
      else await http.post("/expenses", payload);
      toast.success(editId ? "Expense updated" : "Expense added");
      setOpen(false); load();
    } catch (e) { toast.error(requestErrorMessage(e, editId ? "update expense" : "add expense")); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete expense?")) return;
    try { await http.delete(`/expenses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "delete expense")); }
  };
  const submit = async (id) => {
    try { await http.post(`/expenses/${id}/submit`); toast.success("Submitted for approval"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "submit expense")); }
  };
  const uploadReceipt = async (id, file) => {
    if (!file) return;
    try { const fd = new FormData(); fd.append("file", file); await http.post(`/expenses/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Receipt attached"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "attach expense receipt")); }
  };
  const STATUS = { draft: "bg-slate-100 text-slate-600", submitted: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700", rejected: "bg-rose-50 text-rose-700", paid: "bg-brand-50 text-brand-700" };

  const total = rows.reduce((s, e) => s + Number(e.total || e.amount || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Expenses</h1>
          <p className="text-sm text-muted-foreground">{rows.length} expenses · total <span className="font-mono text-foreground">{inr(total)}</span></p>
        </div>
        <Button onClick={openNew} data-testid="add-expense-button"><Plus className="h-4 w-4 mr-1" /> Add Expense</Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {loadError && <div role="alert" className="flex items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
        <div className="p-4 border-b">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search payee, category, description…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="expense-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-medium">Expense No</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Payee</th><th className="px-4 py-3 font-medium">Description</th>
              <th className="px-4 py-3 font-medium">Mode</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium text-right">GST</th>
              <th className="px-4 py-3 font-medium text-right">Total</th><th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-t hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-3 font-mono font-medium">{e.expense_no}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.expense_date}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded capitalize">{e.category}</span></td>
                  <td className="px-4 py-3">{e.payee_name || (e.payee_type === "employee" ? "Employee" : "—")}{(e.attachments || []).length > 0 && <Paperclip className="inline h-3 w-3 ml-1 text-brand-600" />}</td>
                  <td className="px-4 py-3 max-w-[200px] truncate">{e.description}</td>
                  <td className="px-4 py-3 text-xs">{e.payment_mode}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded capitalize ${STATUS[e.status] || STATUS.draft}`}>{e.status || "draft"}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{e.gst_amount ? inr(e.gst_amount) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-rose-600">{inr(e.total || e.amount)}</td>
                  <td className="px-4 py-3"><div className="flex justify-end gap-1 items-center">
                    {(!e.status || e.status === "draft" || e.status === "rejected") && <button onClick={() => submit(e.id)} className="p-1.5 hover:bg-amber-50 rounded" title="Submit for approval" data-testid={`submit-expense-${e.id}`}><Send className="h-4 w-4 text-amber-600" /></button>}
                    <label className="p-1.5 hover:bg-slate-100 rounded cursor-pointer" title="Attach receipt"><UploadCloud className="h-4 w-4 text-slate-600" /><input type="file" className="hidden" accept="image/*,application/pdf" onChange={(ev) => uploadReceipt(e.id, ev.target.files?.[0])} data-testid={`attach-expense-${e.id}`} /></label>
                    <button onClick={() => openEdit(e)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-expense-${e.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
                    <button onClick={() => del(e.id)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`delete-expense-${e.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
                  </div></td>
                </tr>
              ))}
              {!loadError && rows.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-muted-foreground"><Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />No expenses yet</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Expense" : "Add Expense"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={form.expense_date} onChange={(e) => patch("expense_date", e.target.value)} className="mt-1.5" data-testid="expense-date-input" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => patch("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payee Type</Label>
              <Select value={form.payee_type} onValueChange={(v) => patch("payee_type", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="medical_rep">Medical Rep</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Payee Name</Label><Input value={form.payee_name} onChange={(e) => patch("payee_name", e.target.value)} className="mt-1.5" placeholder="Name / vendor" data-testid="expense-payee-input" /></div>
            <div className="sm:col-span-2"><Label>Description *</Label><Input value={form.description} onChange={(e) => patch("description", e.target.value)} className="mt-1.5" data-testid="expense-description-input" /></div>

            {form.category === "travel" && (
              <>
                <div>
                  <Label>Travel Mode</Label>
                  <Select value={form.travel_mode} onValueChange={(v) => patch("travel_mode", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="expense-travelmode-select"><SelectValue placeholder="Select mode" /></SelectTrigger>
                    <SelectContent>{TRAVEL.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {mileage && <div><Label>Distance (km)</Label><Input type="number" value={form.distance_km} onChange={(e) => patch("distance_km", e.target.value)} className="mt-1.5" data-testid="expense-distance-input" /><div className="text-xs text-muted-foreground mt-1">Auto @ ₹{form.travel_mode === "bike" ? "3.25" : "6"}/km</div></div>}
              </>
            )}
            {!mileage && <div><Label>Amount (₹) *</Label><Input type="number" value={form.amount} onChange={(e) => patch("amount", e.target.value)} className="mt-1.5" data-testid="expense-amount-input" /></div>}

            <div><Label>GST %</Label><Input type="number" value={form.gst_pct} onChange={(e) => patch("gst_pct", e.target.value)} className="mt-1.5" /></div>
            <div>
              <Label>Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => patch("payment_mode", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 bg-brand-50 border border-brand-200 rounded px-3 py-2 text-sm flex justify-between" data-testid="expense-total-preview">
              <span>Base {inr(baseAmt)} + GST {inr(gstAmt)}</span>
              <span className="font-semibold">Total {inr(baseAmt + gstAmt)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-expense-button">Save Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
